/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useEffect } from 'react';
import envConfig from '../config/envConfig';
import { useTheme } from '../theme/ThemeContext';
import MenuOptionRegular from './MenuOptionRegular';
import '../styles/MenuOptionRegular.css'
import { useNavigate } from 'react-router-dom';
import { store } from '../utils/storageHelper';
import APIHelper from '../api/APIHelper';
import ProgressBarDialog from './common/ProgressBar';
import AlertDialog from './common/AlertDialog';
import GaneshaLogo from './GaneshaLogo';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

const menuItems = [
  // { icon: 'bi-house', label: 'Home' },
  { icon: 'bi-speedometer2', label: 'Dashboard', to: '/dashboard' },
  { icon: 'bi-app-indicator', label: 'Applications', to: '/dashboard/applications' },
  { icon: 'bi-box', label: 'Marketplace', to: '/dashboard/marketplace' },
  { icon: 'bi-box-seam', label: 'My Packages', to: '/dashboard/my-packages' },
  { icon: 'bi-gear', label: 'Settings', to: '/dashboard/settings' },
  { icon: 'bi-person', label: 'Profile', to: '/dashboard/profile' },
];

function SidebarRegular({ onMenuChange, currentMenu }) {
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);

  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("An error occurred while logging out.");

  const handleLogout = async () => {
    // prevent double clicks
    if (loggingOut) return;

    setLoggingOut(true);

    try {
      const response = await apiHelper.apiCall("/arcee/user/logout/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        let body = null;
        try { body = await response.json(); } catch (e) { body = null; }

        const message =
          (body && (body.error || body.message || body.detail || JSON.stringify(body))) ||
          `Logout failed (status ${response.status})`;
        setErrorDialogMessage(message);
        setErrorDialogOpen(true);

        setLoggingOut(false);
        return;
      }

      // Success — clear client-side state
      store('userData', null);
      store('access_token', null);

      // Navigate to sign-in page
      navigate('/sign-in', { replace: true });
    } catch (err) {
      console.error("Logout error:", err);

      let message = "Logout failed. Please try again.";
      if (err && err.type === "session_expired") {
        message = "Your session has expired. Please login again.";
      } else if (err && err.message) {
        message = err.message;
      }

      setErrorDialogMessage(message);
      setErrorDialogOpen(true);
    } finally {
      setLoggingOut(false);
    }
  }

  // Collapse sidebar automatically on mobile resolutions
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setCollapsed(true);
      } else {
        setCollapsed(false);
      }
    };
    handleResize(); // initial call
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <aside
      className={`sidebar d-flex flex-column align-items-center${collapsed ? ' collapsed' : ''}`}
      style={{
        height: "100%",
        width: collapsed ? 80 : 260,
        flexShrink: 0,
        overflowY: "auto",
        overflowX: "hidden",
        boxShadow: '10px 0 40px rgba(0,0,0,0.1)',
        transition: 'width 0.3s cubic-bezier(0.2, 0, 0, 1)',
        // Sophisticated gradient background
        background: `linear-gradient(170deg, ${theme.primary} 0%, ${theme.primaryLight || theme.primary} 45%, ${theme.secondary} 100%)`,
        zIndex: 100,
        borderRight: '1px solid rgba(255,255,255,0.08)',
        position: 'relative'
      }}
    >
      {/* Decorative Techno Shapes / Gloss Effects */}
      <div style={{
        position: 'absolute',
        top: -100,
        left: -50,
        width: 300,
        height: 300,
        background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
        borderRadius: '50%',
        pointerEvents: 'none',
        zIndex: 0
      }}></div>
      <div style={{
        position: 'absolute',
        bottom: 50,
        right: -80,
        width: 250,
        height: 250,
        background: `radial-gradient(circle, ${theme.secondary}40 0%, transparent 70%)`,
        borderRadius: '50%',
        pointerEvents: 'none',
        zIndex: 0,
        filter: 'blur(40px)'
      }}></div>
      <div style={{
        position: 'absolute',
        top: '40%',
        left: -20,
        width: 80,
        height: 80,
        background: 'linear-gradient(45deg, rgba(255,255,255,0.1), transparent)',
        transform: 'rotate(45deg)',
        pointerEvents: 'none',
        zIndex: 0
      }}></div>

      {/* Content Wrapper to sit above shapes */}
      <div className="d-flex flex-column align-items-center w-100 h-100" style={{ backdropFilter: 'blur(3px)', zIndex: 1, background: 'rgba(0,0,0,0.02)' }}>

        {/* Logo Section */}
        <div
          className="py-4 w-100 d-flex flex-column align-items-center"
          style={{
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            marginBottom: '1rem',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), transparent)'
          }}
        >
          {/* Logo and App Name */}
          <div className="d-flex align-items-center px-3" style={{ width: '100%', justifyContent: collapsed ? 'center' : 'flex-start' }}>
            <div style={{
              filter: 'drop-shadow(0 0 10px rgba(255,255,255,0.3))',
              transition: 'transform 0.3s'
            }}>
              <GaneshaLogo
                height={collapsed ? 36 : 42}
                width={collapsed ? 36 : 42}
              />
            </div>
            {!collapsed && (
              <div className="ms-3 d-flex flex-column">
                <span
                  className="fw-bold fs-5 text-white"
                  style={{
                    letterSpacing: '0.5px',
                    lineHeight: 1.2,
                    fontFamily: "'Outfit', 'Inter', sans-serif",
                    textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                >
                  {envConfig.appName}
                </span>
                <span className="small text-white opacity-50" style={{ fontSize: '0.75rem', letterSpacing: '1.5px', textTransform: 'uppercase' }}>Workspace</span>
              </div>
            )}
          </div>
        </div>

        {/* Collapse Button - Floating style */}
        <div className="w-100 px-3 d-flex justify-content-end mb-2">
          {!collapsed && (
            <button
              className="btn btn-sm p-0 text-white opacity-50 hover-opacity-100"
              onClick={() => setCollapsed(true)}
              title="Collapse Sidebar"
              style={{ background: 'transparent', border: 'none' }}
            >
              <i className="bi bi-list-nested fs-4" style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.2))' }}></i>
            </button>
          )}
          {collapsed && (
            <button
              className="btn btn-sm w-100 text-white opacity-50 hover-opacity-100"
              onClick={() => setCollapsed(false)}
              title="Expand Sidebar"
              style={{ background: 'transparent', border: 'none' }}
            >
              <i className="bi bi-list fs-4" style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.2))' }}></i>
            </button>
          )}
        </div>

        {/* Menu Options */}
        <div className="flex-grow-1 w-100 px-3 d-flex flex-column gap-2">
          {menuItems.map(item => (
            <MenuOptionRegular
              key={item.label}
              icon={item.icon}
              label={collapsed ? '' : item.label}
              active={false /* handled by navlink internally usually, but MenuOptionRegular checks location */}
              horizontal
              collapsed={collapsed}
              to={item.to}
            />
          ))}
        </div>

        {/* Logout Option - Bottom Fixed */}
        <div className="w-100 p-3 mt-auto border-top border-white-10" style={{ background: 'rgba(0,0,0,0.1)' }}>
          <div
            className="w-100 d-flex align-items-center py-2 px-3 rounded-3 group-hover-effect"
            onClick={handleLogout}
            style={{
              cursor: 'pointer',
              color: '#ffdddd', // Light red tint for danger/logout
              background: 'rgba(255, 0, 0, 0.05)',
              border: '1px solid rgba(255, 0, 0, 0.05)',
              transition: 'all 0.3s ease',
              justifyContent: collapsed ? 'center' : 'flex-start',
              backdropFilter: 'blur(5px)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 0, 0, 0.2)';
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 53, 69, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 0, 0, 0.05)';
              e.currentTarget.style.color = '#ffdddd';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <i className="bi bi-box-arrow-right" style={{ fontSize: 18, marginRight: collapsed ? 0 : 12 }} />
            {!collapsed && <span style={{ fontSize: 14, fontWeight: 600 }}>Sign Out</span>}
          </div>
        </div>
      </div>

      {/* Progress dialog (blocks UI while logging out) */}
      <ProgressBarDialog
        open={loggingOut}
        text_message="Logging out..."
        cancellable={false}
      />

      {/* Error dialog - using your AlertDialog component */}
      <AlertDialog
        open={errorDialogOpen}
        title="Logout failed"
        message={errorDialogMessage}
        type="error"
        okLabel="OK"
        showCancel={false}
        onOk={() => setErrorDialogOpen(false)}
        onCancel={() => setErrorDialogOpen(false)}
        mini_mode={true}
      />
    </aside>
  );
}

export default SidebarRegular;
